import React, { useState } from 'react';
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { Truck, Clock, CheckCircle2, Package } from 'lucide-react';

export default function OrderTracking() {
  const [orderId, setOrderId] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!orderId.trim()) return;

    setLoading(true);
    try {
      const docRef = doc(db, 'orders', orderId.trim());
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setOrder({ id: docSnap.id, ...docSnap.data() });
      } else {
        alert("Order not found! Please check your Order ID.");
        setOrder(null);
      }
    } catch (error) {
      console.error("Error tracking order:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="h-14 w-14 bg-emerald-100 text-emerald-800 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-inner">
            <Truck className="h-7 w-7" />
          </div>
          <h1 className="text-3xl font-black text-gray-900">Track Your Eco-Order</h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">Enter your Order ID received during checkout to check live status.</p>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleTrack} className="bg-white p-4 rounded-2xl shadow-sm border border-gray-200 flex gap-2 mb-8">
          <input 
            type="text" 
            placeholder="Paste Order ID here..." 
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            className="flex-1 p-3.5 border border-gray-200 rounded-xl text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-800"
            required
          />
          <button type="submit" disabled={loading} className="bg-emerald-800 hover:bg-emerald-900 text-white px-6 py-3.5 rounded-xl font-bold text-xs sm:text-sm transition shadow-md">
            {loading ? 'Searching...' : 'Track Order'}
          </button>
        </form>

        {/* Order Details Card */}
        {order && (
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6 sm:p-8 space-y-6">
            <div className="flex justify-between items-center border-b border-gray-100 pb-4">
              <div>
                <span className="text-[10px] text-gray-400 font-extrabold uppercase tracking-widest">Order ID</span>
                <h3 className="font-mono font-bold text-sm sm:text-base text-gray-900">{order.id}</h3>
              </div>
              <div className="text-right">
                <span className="text-[10px] text-gray-400 font-extrabold uppercase tracking-widest">Status</span>
                <div className="flex items-center space-x-1.5 text-emerald-800 font-bold text-xs bg-emerald-50 border border-emerald-100 px-3 py-1.5 rounded-full mt-1">
                  <Clock className="h-3.5 w-3.5" />
                  <span>{order.status || 'Processing'}</span>
                </div>
              </div>
            </div>

            {/* Customer & Shipping Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
              <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100">
                <h4 className="font-extrabold text-gray-700 mb-1">Customer Name</h4>
                <p className="text-gray-900 font-medium">{order.customerDetails?.fullName}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100">
                <h4 className="font-extrabold text-gray-700 mb-1">Shipping Address</h4>
                <p className="text-gray-900 font-medium">{order.customerDetails?.address}, {order.customerDetails?.city} - {order.customerDetails?.pincode}</p>
              </div>
            </div>

            {/* Items List */}
            <div>
              <h4 className="font-extrabold text-gray-800 text-xs uppercase tracking-wider mb-3">Items Ordered</h4>
              <div className="space-y-2">
                {order.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center text-xs bg-gray-50 p-3.5 rounded-2xl border border-gray-100">
                    <span className="font-bold text-gray-800">{item.name} (x{item.quantity})</span>
                    <span className="font-bold text-gray-900">₹{item.price * item.quantity}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center mt-4 pt-3 border-t border-gray-100 font-black text-sm text-gray-900">
                <span>Total Amount Paid</span>
                <span className="text-emerald-950 text-base">₹{order.total}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}